// src/components/MessageViewer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Link as MuiLink,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DOMPurify from "dompurify";
import dayjs from "dayjs";

import { useGraph } from "../../app/auth/useGraph"; // ← relative import
import { useCompanyActivityLogger } from "../../hooks/useCompanyActivityLogger"; // ← relative import
import { BRAND_BLUE } from "../../theme/colors";
import BrandSpinner from "../../shared/components/BrandSpinner";

/**
 * Props:
 * - messageId: Graph message id (string, required)
 * - autoLog: boolean (default true) — automatically upsert to /company-activity/email/upsert on first load
 * - onLogged: (payload) => void — callback after logging succeeds (e.g., refresh your timeline list)
 * - companyId: number (optional) — force-associate the email to this company
 */
export default function MessageViewer({
  messageId,
  autoLog = true,
  onLogged,
  companyId,
}) {
  const { graphGet } = useGraph();
  const { logEmailById } = useCompanyActivityLogger({ companyId });

  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState(null);

  // Load (and optionally log) the message
  async function loadMessage(doLog = autoLog) {
    if (!messageId) return;
    const encodedMessageId = encodeURIComponent(messageId);
    setError("");
    setLoading(true);
    try {
      let data;
      if (doLog) {
        setLogging(true);
        // logEmailById fetches the full message and posts to your backend; returns the message
        data = await logEmailById(messageId);
        setLogging(false);
        if (onLogged) onLogged({ messageId, logged: true });
      } else {
        data = await graphGet(
          `/me/messages/${encodedMessageId}?$expand=attachments`,
        );
      }
      setMsg(data || null);
    } catch (e) {
      setError(e?.message || "Failed to load message");
    } finally {
      setLoading(false);
      setLogging(false);
    }
  }

  useEffect(() => {
    loadMessage(autoLog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const subject = msg?.subject || "(no subject)";
  const from = msg?.from?.emailAddress;
  const to = msg?.toRecipients || [];
  const cc = msg?.ccRecipients || [];
  const received = msg?.receivedDateTime || msg?.sentDateTime;

  const cleanBodyHtml = useMemo(() => {
    const html = msg?.body?.content || "";
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }, [msg]);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subject}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Reload message (no logging)">
            <span>
              <IconButton onClick={() => loadMessage(false)} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              autoLog
                ? "This email was logged on load"
                : "Log to Company Activity now"
            }
          >
            <span>
              <IconButton
                onClick={() => loadMessage(true)}
                disabled={loading || logging}
              >
                <EmailIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && (
        <BrandSpinner compact py={6} />
      )}

      {!loading && error && (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && msg && (
        <>
          <Box sx={{ mt: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                From:
              </Typography>
              {from && (
                <Chip
                  size="small"
                  icon={<EmailIcon fontSize="small" />}
                  label={`${from.name || from.address} <${from.address}>`}
                  variant="outlined"
                />
              )}
            </Stack>

            {to.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  To:
                </Typography>
                {to.map((r, idx) => (
                  <Chip
                    key={`to-${idx}`}
                    size="small"
                    label={`${r.emailAddress?.name || r.emailAddress?.address} <${r.emailAddress?.address}>`}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}

            {cc.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Cc:
                </Typography>
                {cc.map((r, idx) => (
                  <Chip
                    key={`cc-${idx}`}
                    size="small"
                    label={`${r.emailAddress?.name || r.emailAddress?.address} <${r.emailAddress?.address}>`}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}

            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 1 }}
            >
              {received && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {dayjs(received).format("ddd, MMM D, YYYY h:mm A")}
                </Typography>
              )}
              {msg?.webLink && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Tooltip title="Open in Outlook on the web">
                    <MuiLink
                      href={msg.webLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <IconButton size="small">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </MuiLink>
                  </Tooltip>
                </>
              )}
            </Stack>
          </Box>

          {/* Attachments (metadata only; content fetch is separate) */}
          {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1, flexWrap: "wrap" }}
            >
              <AttachFileIcon fontSize="small" />
              {msg.attachments.map((a, i) => (
                <Chip
                  key={`att-${i}`}
                  size="small"
                  label={`${a.name || "Attachment"}${a.size ? ` • ${a.size} bytes` : ""}`}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Email body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              borderRadius: 1,
              border: "1px solid",
              borderColor: BRAND_BLUE,
              boxShadow: "inset 0 0 0 1px rgba(0, 51, 108, 0.08)",
              p: 2,
              backgroundColor: "background.paper",
              "& img": { maxWidth: "100%", height: "auto" },
              "& table": { width: "100%", borderCollapse: "collapse" },
            }}
          >
            {/* We sanitized with DOMPurify above */}
            <div dangerouslySetInnerHTML={{ __html: cleanBodyHtml }} />
          </Box>

          {logging && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 1 }}
            >
              Logging to Company Activity…
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}
